import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Loader } from 'lucide-react';
import { stripeAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Pricing = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await stripeAPI.getPlans();
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Errore caricamento piani:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (priceId) => {
    if (!user) {
      navigate('/register');
      return;
    }

    setProcessingPlan(priceId);

    try {
      const response = await stripeAPI.createCheckoutSession(priceId);
      // Reindirizza a Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Errore creazione checkout:', error);
      alert('Errore durante la creazione della sessione di pagamento');
      setProcessingPlan(null);
    }
  };

  const parseFeatures = (featuresStr) => {
    if (!featuresStr) return [];
    try {
      return JSON.parse(featuresStr);
    } catch {
      return featuresStr.split(',').map(f => f.trim());
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Caricamento piani...</p>
      </div>
    );
  }

  // Piani di esempio se non sono stati ancora configurati nel database
  const defaultPlans = [
    {
      id: 'starter',
      name: 'Starter',
      price: 49,
      stripe_price_id: 'price_starter',
      description: 'Perfetto per iniziare',
      features: [
        '1 Agente AI',
        'Fino a 500 conversazioni/mese',
        'Gestione appuntamenti base',
        'Support via email',
        'Dashboard analytics'
      ]
    },
    {
      id: 'professional',
      name: 'Professional',
      price: 99,
      stripe_price_id: 'price_professional',
      description: 'Per business in crescita',
      features: [
        '3 Agenti AI',
        'Fino a 2000 conversazioni/mese',
        'Gestione appuntamenti avanzata',
        'Support prioritario',
        'Analytics avanzate',
        'Integrazioni WhatsApp/Telegram',
        'Personalizzazione avanzata'
      ],
      featured: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 199,
      stripe_price_id: 'price_enterprise',
      description: 'Soluzione completa',
      features: [
        'Agenti AI illimitati',
        'Conversazioni illimitate',
        'Tutte le funzionalità Professional',
        'Support 24/7',
        'Account manager dedicato',
        'White label',
        'API personalizzate',
        'Training personalizzato'
      ]
    }
  ];

  const displayPlans = plans.length > 0 ? plans : defaultPlans;

  return (
    <div>
      <section className="pricing">
        <div className="container">
          <h1 className="section-title">Scegli il Piano Perfetto</h1>
          <p className="section-subtitle">
            Tutti i piani includono 14 giorni di prova gratuita. Nessuna carta di credito richiesta.
          </p>

          <div className="pricing-grid">
            {displayPlans.map((plan, index) => (
              <div 
                key={plan.id || index} 
                className={`pricing-card ${plan.featured ? 'featured' : ''}`}
              >
                {plan.featured && (
                  <div className="pricing-badge">Più Popolare</div>
                )}
                
                <h3>{plan.name}</h3>
                {plan.description && (
                  <p style={{ color: 'var(--gray)', marginBottom: '1rem' }}>
                    {plan.description}
                  </p>
                )}
                
                <div className="pricing-price">
                  €{plan.price}
                  <span>/mese</span>
                </div>

                <ul className="pricing-features">
                  {parseFeatures(plan.features).map((feature, idx) => (
                    <li key={idx}>
                      <Check size={20} />
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSubscribe(plan.stripe_price_id)}
                  disabled={processingPlan === plan.stripe_price_id}
                  className={`btn ${plan.featured ? 'btn-primary' : 'btn-outline'}`}
                  style={{ width: '100%', marginTop: '1rem' }}
                >
                  {processingPlan === plan.stripe_price_id ? (
                    <>
                      <Loader size={18} className="spinner" />
                      Reindirizzamento...
                    </>
                  ) : (
                    'Inizia Ora'
                  )}
                </button>
              </div>
            ))}
          </div>

          <div style={{ 
            textAlign: 'center', 
            marginTop: '3rem',
            padding: '2rem',
            background: 'var(--light-gray)',
            borderRadius: '12px'
          }}>
            <h3 style={{ marginBottom: '1rem' }}>Hai bisogno di un piano personalizzato?</h3>
            <p style={{ color: 'var(--gray)', marginBottom: '1.5rem' }}>
              Contattaci per soluzioni enterprise su misura per le tue esigenze
            </p>
            <a href="mailto:sales@tuodominio.com" className="btn btn-secondary">
              Contatta il Team Vendite
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
